import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { getAdminToken, clearAdminToken } from '../utils/auth';
import { useAppContext } from '../context/AppContext';
import { createApp, updateApp, deleteApp } from '../api/appsApi';
import toast from 'react-hot-toast';

const COLORS = ['#3e7b6f', '#1f5f6e', '#5e9bbf', '#b87a0a', '#8b5cf6', '#b83a42', '#10b981', '#f59e0b'];

const emptyForm = {
  name: '',
  slug: '',
  hasBackend: true,
  packageName: '',
  firebaseProjectId: '',
  baseUrl: '',
  serviceKey: '',
  color: '#3e7b6f',
  description: '',
};

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function AppsPage() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? getAdminToken() : null;
  const {
    apps,
    reloadApps,
    firebaseProjectFilter,
    setFirebaseProjectFilter,
    firebaseProjectIds,
  } = useAppContext();

  const registeredApps = apps.filter((a) => !a.synthetic);

  // Filter app cards by the global Firebase project filter from context/sidebar
  const visibleApps = firebaseProjectFilter
    ? registeredApps.filter((a) => a.firebaseProjectId === firebaseProjectFilter)
    : registeredApps;

  const [showForm, setShowForm] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleLogout = () => {
    clearAdminToken();
    navigate('/login', { replace: true });
  };

  const openCreate = () => {
    setEditingApp(null);
    // Pre-fill firebaseProjectId from the active sidebar filter
    setForm({ ...emptyForm, firebaseProjectId: firebaseProjectFilter || '' });
    setShowForm(true);
  };

  const openEdit = (app) => {
    setEditingApp(app);
    setForm({
      name: app.name,
      slug: app.slug,
      hasBackend: app.hasBackend !== false,
      packageName: app.packageName || '',
      firebaseProjectId: app.firebaseProjectId || '',
      baseUrl: app.baseUrl || '',
      serviceKey: app.serviceKey || '',
      color: app.color || '#3e7b6f',
      description: app.description || '',
    });
    setShowForm(true);
  };

  const handleNameChange = (name) => {
    setForm((f) => ({
      ...f,
      name,
      slug: editingApp ? f.slug : slugify(name),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.packageName) {
      toast.error('Name, slug, and package name are required.');
      return;
    }
    if (form.hasBackend && (!form.baseUrl || !form.serviceKey)) {
      toast.error('Backend URL and service key are required for apps with a backend.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        hasBackend: form.hasBackend,
        packageName: form.packageName,
        firebaseProjectId: form.firebaseProjectId.trim(),
        baseUrl: form.hasBackend ? form.baseUrl : '',
        serviceKey: form.hasBackend ? form.serviceKey : '',
        color: form.color,
        description: form.description,
      };
      if (editingApp) {
        await updateApp(editingApp._id, payload);
        toast.success('App updated.');
      } else {
        await createApp({ ...payload, slug: form.slug });
        toast.success('App registered.');
      }
      setShowForm(false);
      reloadApps();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (app) => {
    if (!window.confirm(`Delete "${app.name}"? This cannot be undone.`)) return;
    setDeletingId(app._id);
    try {
      await deleteApp(app._id);
      toast.success('App deleted.');
      reloadApps();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (app) => {
    try {
      await updateApp(app._id, { isActive: !app.isActive });
      toast.success(app.isActive ? 'App deactivated.' : 'App activated.');
      reloadApps();
    } catch (err) {
      toast.error('Failed to toggle app status.');
    }
  };

  return (
    <div className="min-h-screen bg-surface text-white flex">
      <Sidebar onLogout={handleLogout} />
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-serif text-white">Registered Apps</h1>
            <p className="text-xs text-muted mt-1">
              {firebaseProjectFilter ? (
                <>
                  Showing apps in{' '}
                  <span className="font-mono text-sage-light/80">{firebaseProjectFilter}</span>.
                  {' '}
                  <button
                    type="button"
                    onClick={() => setFirebaseProjectFilter(null)}
                    className="underline hover:text-white transition-colors"
                  >
                    Show all
                  </button>
                </>
              ) : (
                'Manage backends connected to this universal admin. Use the Firebase Project filter in the sidebar to narrow by project.'
              )}
            </p>
          </div>
          <button onClick={openCreate} className="btn-primary shrink-0">
            + Register App
            {firebaseProjectFilter && (
              <span className="ml-1.5 font-mono text-[10px] opacity-70">
                in {firebaseProjectFilter}
              </span>
            )}
          </button>
        </header>

        {/* App grid */}
        {visibleApps.length === 0 ? (
          <div className="card text-center py-12">
            {registeredApps.length === 0 ? (
              <>
                <p className="text-muted text-sm">No apps registered yet.</p>
                <p className="text-muted text-xs mt-1">
                  Register an app backend to start monitoring users and sending targeted notifications.
                </p>
                <button onClick={openCreate} className="btn-primary mt-4">
                  Register your first app
                </button>
              </>
            ) : (
              <>
                <p className="text-muted text-sm">No apps in this Firebase project.</p>
                <p className="text-muted text-xs mt-1">
                  Register a new app and assign it to{' '}
                  <span className="font-mono">{firebaseProjectFilter}</span>.
                </p>
                <button onClick={openCreate} className="btn-primary mt-4">
                  Register app in {firebaseProjectFilter}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleApps.map((app) => (
              <div key={app._id || app.slug} className="card flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div
                    className="w-3 h-3 rounded-full mt-1 shrink-0"
                    style={{ backgroundColor: app.color || '#3e7b6f' }}
                  />
                  <div className="flex-1 min-w-0">
                    {/* Name + status */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white text-sm truncate">{app.name}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                          app.isActive ? 'bg-teal-mid/20 text-teal-mid' : 'bg-rose/20 text-rose'
                        }`}
                      >
                        {app.isActive ? 'active' : 'inactive'}
                      </span>
                    </div>

                    {/* Slug */}
                    <p className="text-[11px] text-muted mt-0.5 font-mono">{app.slug}</p>

                    {/* Description */}
                    {app.description && (
                      <p className="text-xs text-muted mt-1">{app.description}</p>
                    )}

                    {/* Firebase project ID — prominent badge */}
                    <div className="mt-1.5">
                      {app.firebaseProjectId ? (
                        <button
                          type="button"
                          onClick={() => setFirebaseProjectFilter(
                            firebaseProjectFilter === app.firebaseProjectId
                              ? null
                              : app.firebaseProjectId
                          )}
                          className={`inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-full border transition-colors ${
                            firebaseProjectFilter === app.firebaseProjectId
                              ? 'bg-sage-light/20 border-sage-light/50 text-sage-light'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:border-sage-light/40 hover:text-sage-light/80'
                          }`}
                          title={`Filter by ${app.firebaseProjectId}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
                          {app.firebaseProjectId}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-600">
                          default project
                        </span>
                      )}
                    </div>

                    {/* Package name / backend URL */}
                    {app.hasBackend === false ? (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium">
                          no backend
                        </span>
                        {app.packageName && (
                          <p
                            className="text-[11px] text-muted font-mono truncate"
                            title={app.packageName}
                          >
                            {app.packageName}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p
                        className="text-[11px] text-muted mt-1 truncate"
                        title={app.baseUrl}
                      >
                        {app.baseUrl}
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-panel-border pt-3 flex gap-2">
                  <button
                    onClick={() => openEdit(app)}
                    className="text-xs text-muted hover:text-white transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(app)}
                    className="text-xs text-muted hover:text-white transition-colors"
                  >
                    {app.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(app)}
                    disabled={deletingId === app._id}
                    className="text-xs text-rose hover:text-rose-light transition-colors ml-auto"
                  >
                    {deletingId === app._id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Register / Edit modal ─────────────────────────────────────── */}
        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
          >
            <div className="bg-panel border border-panel-border rounded-xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  {editingApp ? 'Edit App' : 'Register New App'}
                </h2>
                {form.firebaseProjectId && (
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-sage-light/15 border border-sage-light/30 text-sage-light/80 mt-0.5 flex-shrink-0 ml-3">
                    {form.firebaseProjectId}
                  </span>
                )}
              </div>

              <form onSubmit={handleSave} className="space-y-3">
                <div>
                  <label className="label">App Name</label>
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Wifi Analyzer"
                    required
                  />
                </div>
                <div>
                  <label className="label">Slug (URL-safe identifier)</label>
                  <input
                    className="input font-mono"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="wifi-analyzer"
                    pattern="[a-z0-9-]+"
                    disabled={!!editingApp}
                    required
                  />
                  {editingApp && (
                    <p className="text-[11px] text-muted mt-1">Slug cannot be changed after creation.</p>
                  )}
                </div>
                <div>
                  <label className="label">Package Name</label>
                  <input
                    className="input font-mono"
                    value={form.packageName}
                    onChange={(e) => setForm((f) => ({ ...f, packageName: e.target.value }))}
                    placeholder="com.example.wifianalyzer"
                    required
                  />
                  <p className="text-[11px] text-muted mt-1">
                    Android/iOS bundle ID — Flutter apps register FCM tokens using this identifier.
                  </p>
                </div>

                {/* Firebase Project ID — Dropdown only */}
                <div>
                  <label className="label">Firebase Project ID</label>
                  <div className="relative">
                    <select
                      className="appearance-none w-full bg-panel border border-panel-border text-white text-xs rounded-lg pl-3 pr-7 py-2 focus:outline-none focus:border-sage-mid cursor-pointer font-mono"
                      value={form.firebaseProjectId}
                      onChange={(e) => setForm((f) => ({ ...f, firebaseProjectId: e.target.value }))}
                    >
                      <option value="">Default project</option>
                      {firebaseProjectIds.map((pid) => (
                        <option key={pid} value={pid}>
                          {pid}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted text-[10px]">
                      ▾
                    </span>
                  </div>
                  <p className="text-[11px] text-muted mt-1">
                    Must match a project configured in the backend's{' '}
                    <span className="font-mono">config/firebaseProjects.js</span>.
                  </p>
                </div>

                <div>
                  <label className="label">Backend Base URL</label>
                  <input
                    className={`input ${!form.hasBackend ? 'opacity-40 cursor-not-allowed' : ''}`}
                    value={form.hasBackend ? form.baseUrl : ''}
                    onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                    placeholder="https://wifianalyzer.oxmite.com"
                    type="url"
                    disabled={!form.hasBackend}
                  />
                  <label className="flex items-center gap-2 mt-1.5 cursor-pointer w-fit">
                    <input
                      type="checkbox"
                      checked={!form.hasBackend}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          hasBackend: !e.target.checked,
                          baseUrl: e.target.checked ? '' : f.baseUrl,
                          serviceKey: e.target.checked ? '' : f.serviceKey,
                        }))
                      }
                      className="accent-amber-400 w-3.5 h-3.5"
                    />
                    <span className="text-[11px] text-muted">
                      Not available — this app has no separate backend
                    </span>
                  </label>
                </div>
                <div>
                  <label className="label">
                    Service Key (INTERNAL_SERVICE_KEY from that backend's .env)
                  </label>
                  <input
                    className={`input font-mono ${!form.hasBackend ? 'opacity-40 cursor-not-allowed' : ''}`}
                    value={form.hasBackend ? form.serviceKey : ''}
                    onChange={(e) => setForm((f) => ({ ...f, serviceKey: e.target.value }))}
                    placeholder="long-random-secret-key"
                    disabled={!form.hasBackend}
                  />
                  {!form.hasBackend && (
                    <p className="text-[11px] text-amber-400/70 mt-1">
                      Not available — uses uni_admin_backend for FCM &amp; promos.
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">Color</label>
                  <div className="flex gap-2 flex-wrap">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, color: c }))}
                        className="w-6 h-6 rounded-full border-2 transition-all"
                        style={{
                          backgroundColor: c,
                          borderColor: form.color === c ? '#fff' : 'transparent',
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">Description (optional)</label>
                  <input
                    className="input"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Brief description of this app"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving} className="btn-primary flex-1">
                    {saving ? 'Saving…' : editingApp ? 'Save Changes' : 'Register App'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 py-2 rounded-lg border border-panel-border text-muted text-sm hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
